import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

type Options = Cookies.CookieAttributes;

export const useCookie = <T>(
  cookieName: string,
  initialValue: T,
): [T, (value: T, options?: Options) => void, () => void] => {
  const [value, setValue] = useState<T>(() => {
    const storedValue = Cookies.get(cookieName);
    return storedValue ? JSON.parse(storedValue) : initialValue;
  });

  const updateCookie = (newValue: T, options?: Options) => {
    Cookies.set(cookieName, JSON.stringify(newValue), options);
    setValue(newValue);
  };

  const removeCookie = () => {
    Cookies.remove(cookieName);
    setValue(initialValue);
  };

  const isEmptyObject = (obj: any) => !obj || Object.keys(obj).length === 0;

  const shouldUpdateState = useCallback(() => {
    const storedValue = Cookies.get(cookieName);
    const isEmptyValues = isEmptyObject(storedValue) && isEmptyObject(initialValue);
    const isEqualsValues = storedValue === JSON.stringify(value);

    return !isEmptyValues && !isEqualsValues;
  }, [cookieName, initialValue, value]);

  useEffect(() => {
    const storedValue = Cookies.get(cookieName);

    if (shouldUpdateState()) {
      if (storedValue) {
        setValue(JSON.parse(storedValue));
      } else {
        setValue(initialValue);
      }
    }
  }, [cookieName, initialValue, shouldUpdateState]);

  return [value, updateCookie, removeCookie];
};
